import type { FilterOption } from './components/posFilterTypes';

// Mirrors the API sentinel meaning "no competitor" (client_refs.NO_RIVALS_ID).
export const NO_RIVALS_ID = 0;

type MarketWithYears = { id: number | string; survey_years?: string[] };

// Survey dates offered for premium coverage: the dates of the given markets
// (latest first).
export const getMarketSurveyYears = (
  markets: MarketWithYears[],
  selectedIds: (number | string)[]
): string[] => {
  const selected = new Set(selectedIds.map(String));
  const years = new Set<string>();
  markets
    .filter((market) => selected.has(String(market.id)))
    .forEach((market) =>
      (market.survey_years || []).forEach((year) => years.add(year))
    );
  return Array.from(years).sort((a, b) => b.localeCompare(a));
};

// An empty market selection means every market the client has rights to.
export const getPremiumSurveyYears = (
  markets: MarketWithYears[],
  selectedIds: (number | string)[]
): string[] =>
  getMarketSurveyYears(
    markets,
    selectedIds.length > 0 ? selectedIds : markets.map((market) => market.id)
  );

// Selected markets that have no survey for `year` (premium coverage).
export const getMarketsMissingSurvey = (
  markets: (MarketWithYears & { name: string })[],
  selectedIds: (number | string)[],
  year: string
): string[] => {
  const selected = new Set(selectedIds.map(String));
  return markets
    .filter(
      (market) =>
        selected.has(String(market.id)) &&
        !(market.survey_years || []).includes(year)
    )
    .map((market) => market.name);
};

// Competitors offered for the selected products/brands: the union of their
// rivals (every configured rival while nothing is selected).
export const getScopedRivalOptions = (
  selected: { value: string | number }[],
  rivalsByRef: Record<string, number[]> | undefined,
  rivals: { id: number; name: string }[]
): { value: string; label: string }[] => {
  const rivalIds = new Set(
    selected.flatMap((ref) => rivalsByRef?.[String(ref.value)] ?? [])
  );
  return rivals
    .filter((rival) => selected.length === 0 || rivalIds.has(rival.id))
    .map((rival) => ({ value: String(rival.id), label: rival.name }));
};

// Product selection as API query fields (null = no restriction).
export const buildSelectedRefQuery = (filters: {
  clientRefs: FilterOption[];
  clientCustomRefs: FilterOption[];
}) => ({
  selected_client_ref_ids: toIds(filters.clientRefs),
  selected_client_custom_ids: toIds(filters.clientCustomRefs)
});

const toIds = (options: FilterOption[]): number[] | null =>
  options.length > 0 ? options.map((option) => Number(option.value)) : null;

// Same array when nothing was dropped, so state setters can bail out.
export const keepOffered = <T>(
  items: T[],
  offered: Set<string>,
  keyOf: (item: T) => string
): T[] => {
  const kept = items.filter((item) => offered.has(keyOf(item)));
  return kept.length === items.length ? items : kept;
};
